if (!customElements.get("byte-shop-the-look")) {
  class ByteShopTheLook extends HTMLElement {
    constructor() {
      super();

      this.lookIndex = 0;

      this.productIndex = 0;

      this.isScrollMode = false;

      this.boundHandleClick = this.handleClick.bind(this);

      this.boundHandleScroll = this.#handleScroll.bind(this);

      this.boundHandleResize = this.#handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();

      this.#bindEvents();

      this.#handleResize();

      this.#setLookIndex(0, false);
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest("[data-shop-the-look-action]");

      if (!actionTrigger) return;

      const shopTheLookAction = actionTrigger.dataset.shopTheLookAction;

      if (shopTheLookAction === "select-product") {
        const lookIndex = Number(actionTrigger.dataset.shopTheLookLookIndex);

        const productIndex = Number(actionTrigger.dataset.shopTheLookIndex);

        if (lookIndex !== this.lookIndex) {
          this.#setLookIndex(lookIndex, false);
        }

        this.#setProductIndex(productIndex);

        return;
      }

      if (shopTheLookAction === "prev-look") {
        this.#setLookIndex(this.lookIndex - 1);

        return;
      }

      if (shopTheLookAction === "next-look") {
        this.#setLookIndex(this.lookIndex + 1);

        return;
      }

      if (shopTheLookAction === "prev-product") {
        this.#setProductIndex(this.productIndex - 1);

        return;
      }

      if (shopTheLookAction === "next-product") {
        this.#setProductIndex(this.productIndex + 1);
      }
    }

    #setLookIndex(index, shouldAnimate = true) {
      if (!this.imageSlides.length) return;

      const lookCount = this.imageSlides.length;

      const normalizedIndex = ((index % lookCount) + lookCount) % lookCount;

      if (normalizedIndex === this.lookIndex && shouldAnimate) return;

      this.lookIndex = normalizedIndex;

      this.productIndex = 0;

      for (const [slideIndex, imageSlide] of this.imageSlides.entries()) {
        const isActive = slideIndex === normalizedIndex;

        imageSlide.classList.toggle("is-active", isActive);

        imageSlide.setAttribute("aria-hidden", isActive ? "false" : "true");
      }

      for (const lookProducts of this.lookProductsPanels) {
        const panelLookIndex = Number(
          lookProducts.dataset.shopTheLookLookIndex,
        );

        const isActive = panelLookIndex === normalizedIndex;

        lookProducts.classList.toggle("is-active", isActive);

        lookProducts.setAttribute("aria-hidden", isActive ? "false" : "true");
      }

      this.#updateActiveLookElements(shouldAnimate);

      this.#updateLookControls();
    }

    #setProductIndex(index, shouldAnimate = true) {
      const activeSlides = this.#getActiveProductSlides();

      if (!activeSlides.length) return;

      const slideCount = activeSlides.length;

      const normalizedIndex = ((index % slideCount) + slideCount) % slideCount;

      if (normalizedIndex === this.productIndex && shouldAnimate) return;

      this.productIndex = normalizedIndex;

      this.#updateActiveLookElements(shouldAnimate);
    }

    #updateActiveLookElements(shouldAnimate = true) {
      const activeSlides = this.#getActiveProductSlides();

      const activeDots = this.#getActiveDots();

      const activeCarousel = this.#getActiveCarousel();

      for (const [slideIndex, slide] of activeSlides.entries()) {
        const isActive = slideIndex === this.productIndex;

        slide.classList.toggle("is-active", isActive);

        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      }

      for (const [dotIndex, dot] of activeDots.entries()) {
        dot.setAttribute(
          "aria-current",
          dotIndex === this.productIndex ? "true" : "false",
        );
      }

      if (this.isScrollMode && activeCarousel) {
        const activeSlide = activeSlides[this.productIndex];

        if (activeSlide) {
          activeSlide.scrollIntoView({
            behavior: shouldAnimate ? "smooth" : "instant",

            inline: "start",

            block: "nearest",
          });
        }
      }

      this.#updateProductControls();
    }

    #getActiveProductSlides() {
      return this.slides.filter(
        (slide) =>
          Number(slide.dataset.shopTheLookLookIndex) === this.lookIndex,
      );
    }

    #getActiveDots() {
      return this.dots.filter(
        (dot) => Number(dot.dataset.shopTheLookLookIndex) === this.lookIndex,
      );
    }

    #getActiveCarousel() {
      const activePanel = this.lookProductsPanels.find(
        (panel) =>
          Number(panel.dataset.shopTheLookLookIndex) === this.lookIndex,
      );

      return (
        activePanel?.querySelector("[data-shop-the-look-carousel]") ?? null
      );
    }

    #updateLookControls() {
      if (!this.prevLookButton || !this.nextLookButton) return;

      const hasMultipleLooks = this.imageSlides.length > 1;

      this.prevLookButton.disabled = !hasMultipleLooks;

      this.nextLookButton.disabled = !hasMultipleLooks;
    }

    #updateProductControls() {
      const activePanel = this.lookProductsPanels.find(
        (panel) =>
          Number(panel.dataset.shopTheLookLookIndex) === this.lookIndex,
      );

      if (!activePanel) return;

      const prevButton = activePanel.querySelector(
        '[data-shop-the-look-action="prev-product"]',
      );

      const nextButton = activePanel.querySelector(
        '[data-shop-the-look-action="next-product"]',
      );

      const activeSlides = this.#getActiveProductSlides();

      const hasMultipleProducts = activeSlides.length > 1;

      if (prevButton) prevButton.disabled = !hasMultipleProducts;

      if (nextButton) nextButton.disabled = !hasMultipleProducts;
    }

    #handleScroll() {
      const activeCarousel = this.#getActiveCarousel();

      if (!this.isScrollMode || !activeCarousel) return;

      window.clearTimeout(this.scrollTimeout);

      this.scrollTimeout = window.setTimeout(() => {
        const activeSlides = this.#getActiveProductSlides();

        if (!activeSlides.length) return;

        const carouselRect = activeCarousel.getBoundingClientRect();

        const carouselCenter = carouselRect.left + carouselRect.width / 2;

        let closestIndex = 0;

        let closestDistance = Infinity;

        for (const [slideIndex, slide] of activeSlides.entries()) {
          const slideRect = slide.getBoundingClientRect();

          const slideCenter = slideRect.left + slideRect.width / 2;

          const distance = Math.abs(slideCenter - carouselCenter);

          if (distance < closestDistance) {
            closestDistance = distance;

            closestIndex = slideIndex;
          }
        }

        if (closestIndex !== this.productIndex) {
          this.productIndex = closestIndex;

          this.#updateActiveLookElements(false);
        }
      }, 100);
    }

    #handleResize() {
      this.isScrollMode = window.matchMedia("(max-width: 749px)").matches;
    }

    #cacheElements() {
      this.imageSlides = [
        ...this.querySelectorAll("[data-shop-the-look-image-slide]"),
      ];

      this.lookProductsPanels = [
        ...this.querySelectorAll("[data-shop-the-look-look-products]"),
      ];

      this.slides = [...this.querySelectorAll("[data-shop-the-look-slide]")];

      this.dots = [...this.querySelectorAll("[data-shop-the-look-dot]")];

      this.prevLookButton = this.querySelector(
        '[data-shop-the-look-action="prev-look"]',
      );

      this.nextLookButton = this.querySelector(
        '[data-shop-the-look-action="next-look"]',
      );
    }

    #bindEvents() {
      this.addEventListener("click", this.boundHandleClick);

      for (const panel of this.lookProductsPanels) {
        const carousel = panel.querySelector("[data-shop-the-look-carousel]");

        carousel?.addEventListener("scroll", this.boundHandleScroll, {
          passive: true,
        });
      }

      window.addEventListener("resize", this.boundHandleResize);
    }

    #unbindEvents() {
      this.removeEventListener("click", this.boundHandleClick);

      for (const panel of this.lookProductsPanels) {
        const carousel = panel.querySelector("[data-shop-the-look-carousel]");

        carousel?.removeEventListener("scroll", this.boundHandleScroll);
      }

      window.removeEventListener("resize", this.boundHandleResize);

      window.clearTimeout(this.scrollTimeout);
    }
  }

  customElements.define("byte-shop-the-look", ByteShopTheLook);
}
